import "./Home.css";
import React, { useState } from "react";
import bunnyLogo from "../../assests/bunny-logo.png";
import validator from "validator";

const API_ENDPOINT = "https://us-central1-hopsavings-6d90f.cloudfunctions.net/api";

async function addEmailToMailingList(email: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_ENDPOINT}/add-to-email-list`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

const ShieldCheckIcon = () => (
  <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6l8-3z"/>
    <path d="M8.5 12l2.5 2.5 4.5-5"/>
  </svg>
);

const BellIcon = () => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8"/>
    <path d="M10 20a2 2 0 0 0 4 0"/>
  </svg>
);

const CheckIcon = () => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12l5 5 9-10"/>
  </svg>
);

const BankIcon = () => (
  <svg aria-hidden="true" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10l9-6 9 6"/>
    <path d="M5 10v9M19 10v9M9 10v9M15 10v9M3 21h18"/>
  </svg>
);

const EyeIcon = () => (
  <svg aria-hidden="true" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const LockIcon = () => (
  <svg aria-hidden="true" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="10" width="16" height="11" rx="3"/>
    <path d="M8 10V7a4 4 0 0 1 8 0v3"/>
  </svg>
);

const TrashIcon = () => (
  <svg aria-hidden="true" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/>
  </svg>
);

const InstagramIcon = () => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="3" width="18" height="18" rx="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r=".8" fill="currentColor"/>
  </svg>
);

const MailIcon = () => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2"/>
    <path d="M3 7l9 6 9-6"/>
  </svg>
);

const HelpIcon = () => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="9"/>
    <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5v.4"/>
    <circle cx="12" cy="17" r=".6" fill="currentColor"/>
  </svg>
);

interface EmailFormProps {
  id: string;
  variant?: "hero" | "signup";
}

const EmailForm: React.FC<EmailFormProps> = ({ id, variant = "hero" }) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const isValidEmail = validator.isEmail(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail || isLoading) return;
    
    setIsLoading(true);
    const success = await addEmailToMailingList(email);
    setStatus(success ? "success" : "error");
    setIsLoading(false);
    if (success) setEmail("");
  };

  const resetForm = () => {
    setStatus("idle");
    setEmail("");
  };

  if (status === "success") {
    return (
      <div className="form-message form-message--success" role="status" aria-live="polite">
        <p>You're on the list! We'll let you know when we launch.</p>
        <button type="button" className="text-button" onClick={resetForm}>
          Add another email
        </button>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="form-message form-message--error" role="status" aria-live="polite">
        <p>Something went wrong. Please try again.</p>
        <button type="button" className="text-button" onClick={resetForm}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <form className={`email-form email-form--${variant}`} onSubmit={handleSubmit}>
      <label htmlFor={`email-input-${id}`} className="visually-hidden">
        Email address
      </label>
      <input
        id={`email-input-${id}`}
        type="email"
        className={`email-input email-input--${variant}`}
        placeholder="you@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={isLoading}
        required
        aria-invalid={email.length > 0 && !isValidEmail}
      />
      <button
        type="submit"
        className="btn btn--primary"
        disabled={!isValidEmail || isLoading}
        aria-busy={isLoading}
      >
        {isLoading ? "Sending..." : "Notify me"}
      </button>
    </form>
  );
};

const PhoneMockupWelcome = () => (
  <div className="phone-mockup">
    <div className="phone-screen">
      <div className="phone-header">
        <span className="phone-title">Welcome</span>
        <span className="phone-help-btn"><HelpIcon /></span>
      </div>
      <div className="app-card app-card--navy">
        <span className="app-card__label">Total saved</span>
        <span className="app-card__amount">$1,250.00</span>
        <span className="app-card__sub">2 goals in progress</span>
      </div>
      
      <div className="goal-card">
        <div className="goal-card__header">
          <span className="goal-card__name">Trip</span>
          <span className="goal-card__amounts">$900 / $4,444</span>
        </div>
        <div className="progress-bar">
          <div className="progress-bar__fill" style={{ width: "20%" }}></div>
        </div>
        <div className="goal-card__next">Next save: $83.85 on Friday</div>
      </div>
      
      <div className="goal-card">
        <div className="goal-card__header">
          <span className="goal-card__name">Emergency fund</span>
          <span className="goal-card__amounts">$350 / $2,400</span>
        </div>
        <div className="progress-bar">
          <div className="progress-bar__fill" style={{ width: "15%" }}></div>
        </div>
        <div className="goal-card__next">Next save: $92.31 on Monday</div>
      </div>
      
      <div className="app-btn app-btn--navy">Add a goal</div>
    </div>
  </div>
);

const PhoneMockupPlan = () => (
  <div className="phone-mockup phone-mockup--offset">
    <div className="phone-screen">
      <span className="phone-title">Here's your plan</span>
      <div className="app-card app-card--navy app-card--plan">
        <span className="plan-goal-name">Trip</span>
        <span className="plan-amount">$83.85</span>
        <span className="plan-freq">every week, 53 times</span>
        <div className="plan-timeline">
          <span className="timeline-dot timeline-dot--start"></span>
          <span className="timeline-line"></span>
          <span className="timeline-dot timeline-dot--end"></span>
        </div>
        <div className="plan-dates">
          <span>Today</span>
          <span>Oct 25, 2027</span>
        </div>
      </div>
      
      <div className="plan-details">
        <div className="plan-row">
          <span className="plan-row__label">Target amount</span>
          <span className="plan-row__value">$4,444.00</span>
        </div>
        <div className="plan-row plan-row--last">
          <span className="plan-row__label">Number of saves</span>
          <span className="plan-row__value">53 weekly</span>
        </div>
      </div>
      
      <div className="plan-note">
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3F6FA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6l8-3z"/>
          <path d="M8.5 12l2.5 2.5 4.5-5"/>
        </svg>
        <span>You save in your own bank. We only read your linked savings or checking balance, and we never move your money.</span>
      </div>
      
      <div className="app-btn app-btn--navy">Start saving</div>
    </div>
  </div>
);

const Home: React.FC = () => {
  return (
    <div className="page">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      
      {/* NAV */}
      <header className="nav-header">
        <nav className="nav">
          <a href="#top" className="nav__brand" aria-label="HopSavings home">
            <span className="nav__logo-circle">
              <img src={bunnyLogo} alt="" className="nav__logo" />
            </span>
            <span className="nav__name">HopSavings</span>
          </a>
          <div className="nav__links">
            <a href="#how" className="nav__link">How it works</a>
            <a href="#safe" className="nav__link">Your money</a>
            <a href="#signup" className="nav__cta">Get early access</a>
          </div>
        </nav>
      </header>

      <main id="main-content">

      {/* HERO */}
      <section id="top" className="hero">
        <div className="hero__inner">
          <div className="hero__content">
            <span className="badge">Launching soon</span>
            <h1 className="hero__title">Build your savings habit, one hop at a time.</h1>
            <p className="hero__text">
              Pick a goal, get a simple plan, and see your progress every time you save. 
              All of it happens in the bank account you already have.
            </p>
            <EmailForm id="hero" variant="hero" />
            <div className="hero__trust">
              <ShieldCheckIcon />
              <p>Be first to know when we go live, and you might get early access. No spam, ever.</p>
            </div>
          </div>
          <div className="hero__phones">
            <PhoneMockupWelcome />
            <PhoneMockupPlan />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="how">
        <div className="how__inner">
          <div className="how__header">
            <span className="eyebrow">How it works</span>
            <h2 className="how__title">Three hops to your first goal.</h2>
          </div>
          <div className="how__cards">
            <div className="how-card">
              <span className="how-card__number">1</span>
              <h3 className="how-card__title">Link your bank</h3>
              <p className="how-card__text">
                Connect the account you already save in. It's a read-only connection 
                through Plaid, so we can see progress but never touch your money.
              </p>
              <div className="how-card__visual">
                <div className="checklist">
                  <div className="checklist__item checklist__item--done">
                    <span className="checklist__check"><CheckIcon /></span>
                    <span className="checklist__text">Link your bank</span>
                  </div>
                  <div className="checklist__item">
                    <span className="checklist__num">2</span>
                    <span className="checklist__text checklist__text--active">Create your goal</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="how-card">
              <span className="how-card__number">2</span>
              <h3 className="how-card__title">Set a goal</h3>
              <p className="how-card__text">
                Name it, pick an amount and a date. HopSavings works out how much to 
                set aside every week, every two weeks, or every month.
              </p>
              <div className="how-card__visual how-card__visual--pills">
                <span className="chip">Emergency fund</span>
                <span className="chip chip--selected">Trip</span>
                <span className="chip">New car</span>
                <span className="chip">6 mo</span>
                <span className="chip">1 yr</span>
              </div>
            </div>
            
            <div className="how-card">
              <span className="how-card__number">3</span>
              <h3 className="how-card__title">Hop toward it</h3>
              <p className="how-card__text">
                Get a reminder when it's time to save, move the money yourself, 
                and watch your progress climb.
              </p>
              <div className="how-card__visual how-card__visual--reminder">
                <div className="reminder-card">
                  <BellIcon />
                  <span className="reminder-card__text">Time to hop! Save $83.85 for Trip</span>
                </div>
                <div className="progress-bar progress-bar--light">
                  <div className="progress-bar__fill progress-bar__fill--light" style={{ width: "42%" }}></div>
                </div>
                <span className="reminder-progress-text">42% of the way there</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* YOUR MONEY */}
      <section id="safe" className="safe">
        <div className="safe__inner">
          <div className="safe__card">
            <div className="safe__header">
              <span className="eyebrow eyebrow--light">Your money</span>
              <h2 className="safe__title">Your money never leaves your bank.</h2>
              <p className="safe__text">
                HopSavings is a planner and a cheerleader, not a bank. You stay in control of every dollar.
              </p>
            </div>
            <div className="safe__grid">
              <div className="safe-item">
                <BankIcon />
                <h3 className="safe-item__title">We never move your money</h3>
                <p className="safe-item__text">
                  We don't hold, transfer, or invest it, and we can't start payments from your account.
                </p>
              </div>
              <div className="safe-item">
                <EyeIcon />
                <h3 className="safe-item__title">Read-only through Plaid</h3>
                <p className="safe-item__text">
                  We only read your linked savings or checking balance to track your progress.
                </p>
              </div>
              <div className="safe-item">
                <LockIcon />
                <h3 className="safe-item__title">We don't sell your data</h3>
                <p className="safe-item__text">
                  No selling, no ads. We never ask for your SSN, a photo ID, or full account numbers.
                </p>
              </div>
              <div className="safe-item">
                <TrashIcon />
                <h3 className="safe-item__title">Leave anytime</h3>
                <p className="safe-item__text">
                  Delete your account in the app whenever you want, and your data goes with it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNUP */}
      <section id="signup" className="signup">
        <div className="signup__inner">
          <div className="signup__card">
            <span className="signup__logo-circle">
              <img src={bunnyLogo} alt="HopSavings bunny" className="signup__logo" />
            </span>
            <h2 className="signup__title">We're almost there!</h2>
            <p className="signup__text">
              Our app is launching soon. Join the list to hear the moment we go live, 
              and maybe get early access.
            </p>
            <EmailForm id="signup-form" variant="signup" />
          </div>
        </div>
      </section>
      </main>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer__inner">
          <div className="footer__brand">
            <img src={bunnyLogo} alt="" className="footer__logo" />
            <span className="footer__name">HopSavings</span>
          </div>
          <div className="footer__links">
            <a href="/terms" className="footer__link">Terms of Service</a>
            <a href="/privacy" className="footer__link">Privacy Policy</a>
            <a 
              href="https://www.instagram.com/hopsavings/" 
              target="_blank" 
              rel="noreferrer"
              className="footer__social"
              aria-label="HopSavings on Instagram"
            >
              <InstagramIcon />
            </a>
            <a 
              href="mailto:support@hopsavings.com"
              className="footer__social"
              aria-label="Email HopSavings"
            >
              <MailIcon />
            </a>
          </div>
          <span className="footer__copyright">© 2026 HopSavings, LLC. All rights reserved. HopSavings is not a bank.</span>
        </div>
      </footer>
    </div>
  );
};

export default Home;
